import { observer } from 'mobx-react'
import React from 'react'
import styles from './styles.sass'
import {
  TableEmptyStateContainer,
  TableHeader,
  TableHost,
} from '../../../components/Table'
import {
  SelectionState,
  UserColumn,
  UserManagementViewModel,
} from '../../UserManagementViewModel'
import Checkbox from '../../../components/Checkbox'
import UserManagementTableRow from '.././UserManagementTableRow'
import UserManagementTableControls from '.././UserManagementTableControls'
import { match } from '../../../utils/pattern-match-util'
import VirtualTable from '../../../payouts/components/VirtualTable'
import {
  EmptyState,
  EmptyStateIllustrationImage,
} from '../../../components/EmptyState'
import Button from '../../../components/Button'

interface HeaderRowProps {
  columns: Array<UserColumn>
  selected: SelectionState
  toggleAll: (checked: boolean) => void
}

const HeaderRow = observer(
  ({ columns, selected, toggleAll }: HeaderRowProps) => (
    <>
      <TableHeader align="left">
        <Checkbox
          onChange={toggleAll}
          checked={match(selected, {
            [SelectionState.AllSelected]: () => true,
            [SelectionState.NoneSelected]: () => false,
            [SelectionState.SomeSelected]: () =>
              'indeterminate' as boolean | 'indeterminate',
          })}
        />
      </TableHeader>

      {columns
        .filter((col) => col.visible)
        .map((col) => (
          <TableHeader key={col.id} canSort={col.sortable} align="left">
            {col.header}
          </TableHeader>
        ))}

      <TableHeader />
    </>
  )
)

interface TableUserCountProps {
  count: number
}

const TableUserCount = observer(({ count }: TableUserCountProps) => (
  <div className={styles.tableUserCount}>
    Users
    <span className={styles.resultCount}>({count ?? 0})</span>
  </div>
))

interface UserManagementTableProps {
  vm: UserManagementViewModel
}

export default observer(({ vm }: UserManagementTableProps) => (
  <>
    <TableUserCount count={vm.userSearch.total} />

    <TableHost>
      <UserManagementTableControls vm={vm.userSearch} />

      {vm.users.length !== 0 ? (
        <VirtualTable
          overscan={100}
          scroller={vm.userSearch.scrollProvider}
          totalCount={vm.userSearch.total}
          headerContent={() => (
            <HeaderRow
              columns={vm.columns}
              selected={vm.selectedState}
              toggleAll={vm.toggleAll}
            />
          )}
          itemContent={function (index) {
            const user = vm.users[index]

            return (
              <UserManagementTableRow
                key={user.publicId}
                user={user}
                isSelected={vm.isSelected}
                toggleRow={vm.toggleUser}
              />
            )
          }}
        />
      ) : (
        <TableEmptyStateContainer bordered>
          <EmptyState
            title="We couldn't find what you were looking for."
            subtitle={
              <>
                Maybe try{' '}
                <Button size="baseline" type="text">
                  clearing your search filter
                </Button>
                ?
              </>
            }
            small
            image={
              <EmptyStateIllustrationImage
                src={require('assets/images/img_search-noresults.svg')}
              />
            }
          />
        </TableEmptyStateContainer>
      )}
    </TableHost>
  </>
))
